import {
  ERROR_CODES,
  JSONRPC_VERSION,
  type RpcErrorResponse,
  type RpcId,
  type RpcRequest,
  type RpcSuccessResponse,
} from './wsClient.js';

export type ToolHandler<P = unknown, R = unknown> = (params: P) => Promise<R> | R;

export interface ToolCallParams {
  name: string;
  arguments?: unknown;
}

export class Dispatcher {
  private readonly handlers = new Map<string, ToolHandler>();

  register<P, R>(toolName: string, handler: ToolHandler<P, R>): void {
    this.handlers.set(toolName, handler as ToolHandler);
  }

  registerHandler<P, R>(toolName: string, handler: ToolHandler<P, R>): void {
    this.register(toolName, handler);
  }

  has(toolName: string): boolean {
    return this.handlers.has(toolName);
  }

  list(): string[] {
    return [...this.handlers.keys()];
  }

  async dispatch(req: RpcRequest): Promise<RpcSuccessResponse | RpcErrorResponse> {
    const id: RpcId = req.id ?? null;
    const { name, args } = extractCall(req);
    if (!name) {
      return makeError(id, ERROR_CODES.INVALID_PARAMS, 'tool_call requires a name');
    }
    const handler = this.handlers.get(name);
    if (!handler) {
      return makeError(id, ERROR_CODES.METHOD_NOT_FOUND, `unknown tool: ${name}`);
    }
    try {
      const result = await handler(args);
      return makeSuccess(id, result);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'tool handler failed';
      const code =
        err instanceof ToolError ? err.code : ERROR_CODES.INTERNAL_ERROR;
      const data = err instanceof ToolError ? err.data : undefined;
      return data === undefined
        ? makeError(id, code, message)
        : makeErrorWithData(id, code, message, data);
    }
  }
}

export class ToolError extends Error {
  readonly code: number;
  readonly data?: unknown;
  constructor(code: number, message: string, data?: unknown) {
    super(message);
    this.code = code;
    this.data = data;
  }
}

function extractCall(req: RpcRequest): { name: string | null; args: unknown } {
  // Two acceptable shapes:
  //   1. method === "tool_call", params === { name, arguments } (or legacy { name, args })
  //   2. method === "<toolName>",  params === <args>
  if (req.method === 'tool_call' || req.method === 'toolCall') {
    const p = (req.params ?? {}) as ToolCallParams & { args?: unknown };
    return { name: typeof p.name === 'string' ? p.name : null, args: p.arguments ?? p.args };
  }
  return { name: req.method, args: req.params };
}

function makeSuccess(id: RpcId, result: unknown): RpcSuccessResponse {
  return { jsonrpc: JSONRPC_VERSION, id, result };
}

function makeError(id: RpcId, code: number, message: string): RpcErrorResponse {
  return { jsonrpc: JSONRPC_VERSION, id, error: { code, message } };
}

function makeErrorWithData(
  id: RpcId,
  code: number,
  message: string,
  data: unknown,
): RpcErrorResponse {
  return { jsonrpc: JSONRPC_VERSION, id, error: { code, message, data } };
}
